import { ActionIcon, Loader, LoadingOverlay, Modal, Select, Table } from '@mantine/core';
import {
  IconArrowDown,
  IconArrowsDiagonal,
  IconArrowUp,
  IconCaretLeftFilled,
  IconCaretRightFilled,
  IconChevronDown,
  IconPencil,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { LoadingState, useMonkAppState } from '@monkvision/common';
import { clsx } from 'clsx';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { DebouncedState } from 'use-debounce';
import { useAnalytics } from '@monkvision/analytics';
import { decodeMonkJwt } from '@monkvision/network';
import { SteeringWheelPosition, VehicleType } from '@monkvision/types/lib/sights';
import { Icon } from '@monkvision/common-ui-web';
import { PricingColors, TabContent, TabKeys } from '@monkvision/inspection-review';
import { InspectionReviewHOC } from '@monkvision/inspection-review/lib/InspectionReviewHOC';
import { CurrencySymbol } from '@monkvision/types';
import styles from './InspectionListView.module.css';
import {
  useExportToCsv,
  UseInspectionListFiltersResult,
  UseInspectionListResult,
} from '../../hooks';
import { STATUS_TRANSLATION_KEYS } from '../DashboardDrawer';
import { InspectionDetails, isInspectionReviewAvailable } from '../InspectionDetails';
import { InspectionListFilters, InspectionListFiltersProps } from '../InspectionListFilters';
import {
  DashboardInspection,
  DashboardInspectionStatus,
} from '../../hooks/useInspectionList/types';
import {
  SortByProperty,
  SortDirection,
  DashboardInspectionListFilters,
} from '../../hooks/useInspectionListFilters/types';

const TIRES_TAB_KEY = 'Tires';
const OTHER_TAB_KEY = 'Other';

const customTabs: Record<string, TabContent> = {
  [TIRES_TAB_KEY]: {
    Component: () => null,
    onActivate: ({ allGalleryItems, sights, setCurrentGalleryItems }) => {
      const tabSightIds = sights[TIRES_TAB_KEY] ?? [];
      setCurrentGalleryItems(
        allGalleryItems.filter((item) => item.sight && tabSightIds.includes(item.sight.id)),
      );
    },
  },
  [OTHER_TAB_KEY]: {
    Component: () => null,
    onActivate: ({ allGalleryItems, sights, setCurrentGalleryItems }) => {
      const tabSightIds = sights[OTHER_TAB_KEY] ?? [];
      setCurrentGalleryItems(
        allGalleryItems.filter((item) => item.sight && tabSightIds.includes(item.sight.id)),
      );
    },
  },
};

export interface InspectionListProps
  extends Pick<UseInspectionListFiltersResult, 'toggleSortBy'>,
    Pick<
      InspectionListFiltersProps,
      'filterByDate' | 'filterBySearchInput' | 'filterByStatuses' | 'filters'
    >,
    Pick<
      UseInspectionListResult,
      'inspections' | 'pagination' | 'setRowsPerPage' | 'goToNextPage' | 'goToPreviousPage'
    > {
  inspections: DashboardInspection[];
  showStatusColumn: boolean;
  filters: DashboardInspectionListFilters;
  itemsLoading: LoadingState;
  totalLoading: LoadingState;
  currency: string;
  status: DashboardInspectionStatus | null;
  onStatusChange: DebouncedState<(inspectionId: string, value: DashboardInspectionStatus) => void>;
}

function formatLastUpdated(value?: Date): string | undefined {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : undefined;
}

function NoData() {
  const { t } = useTranslation();
  return <span className={styles['noData']}>{t('inspectionList.table.noData')}</span>;
}

export function InspectionListView({
  showStatusColumn,
  filters,
  itemsLoading,
  totalLoading,
  toggleSortBy,
  inspections,
  pagination,
  setRowsPerPage,
  goToNextPage,
  goToPreviousPage,
  currency,
  filterByDate,
  filterBySearchInput,
  filterByStatuses,
  status,
  onStatusChange,
}: InspectionListProps) {
  const [reviewStartTime, setReviewStartTime] = useState<number | null>(null);
  const [detailsOpened, { open: openDetails, close: closeDetails }] = useDisclosure(false);
  const [reviewOpened, { open: openReview, close: closeReview }] = useDisclosure(false);
  const [openedInspection, setOpenedInspection] = useState<DashboardInspection | null>(null);
  const { exportToCsv, loading: exportToCsvLoading } = useExportToCsv({ inspections, currency });
  const { t } = useTranslation();
  const { config, authToken } = useMonkAppState();
  const apiConfig = {
    authToken: authToken ?? '',
    apiDomain: config.apiDomain,
    thumbnailDomain: config.thumbnailDomain,
  };
  const analytics = useAnalytics();

  const paginationDisabled =
    itemsLoading.isLoading ||
    !!itemsLoading.error ||
    totalLoading.isLoading ||
    !!totalLoading.error ||
    inspections.length === 0;
  const handleSortClick = (property: SortByProperty) => {
    if (!paginationDisabled) {
      toggleSortBy(property);
    }
  };

  const selectRowsPerPage = (value: string | null) => {
    if (value) {
      setRowsPerPage(Number(value));
    }
  };

  const SortIcon = filters.sortDirection === SortDirection.ASCENDING ? IconArrowUp : IconArrowDown;
  const sort = <SortIcon size={14} color='black' className={styles['sortIcon']} />;
  const maxColSpan = showStatusColumn ? 4 : 3;

  const openInspectionDetails = (inspection: DashboardInspection) => {
    setOpenedInspection(inspection);
    openDetails();
  };

  const openInspectionReview = (inspection: DashboardInspection) => {
    closeDetails();
    setOpenedInspection(inspection);
    openReview();
    setReviewStartTime(Date.now());
  };

  const handleCloseReview = () => {
    closeReview();
    analytics.trackEvent('Inspection review', {
      inspectionId: openedInspection?.inspectionId,
      duration: reviewStartTime ? (Date.now() - reviewStartTime) / 1000 : undefined,
      authToken: authToken ? decodeMonkJwt(authToken)?.sub : undefined,
    });
    setReviewStartTime(Date.now());
  };

  return (
    <div className={styles['container']}>
      <Modal opened={detailsOpened} onClose={closeDetails}>
        {openedInspection && (
          <InspectionDetails
            inspection={openedInspection}
            onOpenReview={openInspectionReview}
            onStatusChange={onStatusChange}
          />
        )}
      </Modal>
      <Modal
        opened={reviewOpened}
        onClose={handleCloseReview}
        withCloseButton={false}
        size={openedInspection?.inspectionId ? '100dvw' : undefined}
        classNames={{ header: styles['modalHeader'] }}
        closeOnEscape={false}
      >
        {openedInspection?.inspectionId ? (
          <div className={styles['inspectionReviewContainer']}>
            <Icon
              icon='close'
              size={16}
              onClick={handleCloseReview}
              className={styles['closeModalIcon']}
            />

            <InspectionReviewHOC
              lang={'en-hag'}
              apiConfig={apiConfig}
              inspectionId={openedInspection.inspectionId}
              currency={CurrencySymbol.USD}
              additionalInfo={{
                VIN: openedInspection?.vin as string,
              }}
              steeringWheelPosition={SteeringWheelPosition.LEFT}
              vehicleType={VehicleType.SEDAN}
              pricings={{
                none: {
                  color: PricingColors.NONE,
                  min: 0,
                  max: 0,
                },
                damaged: {
                  color: '#f49796',
                  min: 1,
                  max: 9999999,
                },
              }}
              customTabs={customTabs}
              sightsPerTab={{
                [TabKeys.Exterior]: [
                  'haccord-8YjMcu0D',
                  'haccord-DUPnw5jj',
                  'haccord-Z84erkMb',
                  'haccord-cXSAj2ez',
                  'haccord-KN23XXkX',
                  'haccord-PGr3RzzP',
                  'haccord-sorgeRJ7',
                  'haccord-Jq65fyD4',
                  'haccord-ps7cWy6K',
                  'haccord-GdWvsqrm',
                  'haccord-Qel0qUky',
                  'haccord-_YnTubBA',
                  'haccord-GQcZz48C',
                  'haccord-QKfhXU7o',
                  'haccord-hsCc_Nct',
                  'haccord-bSAv3Hrj',
                  'fesc20-H1dfdfvH',
                  'fesc20-WMUaKDp1',
                  'fesc20-Wzdtgqqz',
                  'fesc20-j3H8Z415',
                  'fesc20-dKVLig1i',
                  'fesc20-HYz5ziHi',
                  'fesc20-r_UeXQRO',
                  'fesc20-LZc7p2kK',
                  'fesc20-X8k7UFGf',
                  'fesc20-dfICsfSV',
                  'fesc20-P470Q-jm',
                  'fesc20-26n47kaO',
                  'fesc20-WIQsf_gX',
                  'fesc20-hp3Tk53x',
                  'fesc20-LTe3X2bg',
                  'fesc20-NLdqASzl',
                  'jgc21-QIvfeg0X',
                  'jgc21-KyUUVU2P',
                  'jgc21-__JKllz9',
                  'jgc21-TRN9Des4',
                  'jgc21-s7WDTRmE',
                  'jgc21-1j-oTPag',
                  'jgc21-JerG7oW5',
                  'jgc21-3JJvM7_B',
                  'jgc21-tbF2Ax8v',
                  'jgc21-ezXzTRkj',
                  'jgc21-ESc0HCzy',
                  'jgc21-TEoi50Ff',
                  'jgc21-z15ZdJL6',
                  'jgc21-RE3li6rE',
                  'jgc21-zCrDwYWE',
                  'jgc21-m2dDoMup',
                  'ffocus18-XlfgjQb9',
                  'ffocus18-3TiCVAaN',
                  'ffocus18-lRDlWiwR',
                  'ffocus18-cXSAj2ez',
                  'ffocus18-KkeGvT-F',
                  'ffocus18-FdsQDaTW',
                  'ffocus18-Wo8PkcLF',
                  'ffocus18-jWOq2CNN',
                  'ffocus18-X2LDjCvr',
                  'ffocus18-9MeSIqp7',
                  'ffocus18-vFR9PKjB',
                  'ffocus18-6FX31ty1',
                  'ffocus18-x_1SE7X-',
                  'ffocus18-QKfhXU7o',
                  'ffocus18-43ljK5xC',
                  'ffocus18-cPUyM28L',
                  'ff150-zXbg0l3z',
                  'ff150-3he9UOwy',
                  'ff150-gFp78fQO',
                  'ff150-BmXfb-qD',
                  'ff150-7nvlys8r',
                  'ff150-_UIadfVL',
                  'ff150-2WUJ179s',
                  'ff150-t3KBMPeD',
                  'ff150-nF_oFvhI',
                  'ff150--xPZZd83',
                  'ff150-xbOhu7nK',
                  'ff150-GOx2s_9L',
                  'ff150-FqbrFVr2',
                  'ff150-g_xBOOS2',
                  'ff150-KgHVkQBW',
                  'ff150-V-xzfWsx',
                  'ftransit18-wyXf7MTv',
                  'ftransit18-UNAZWJ-r',
                  'ftransit18-IIVI_pnX',
                  'ftransit18-TkXihCj4',
                  'ftransit18-4NMPqEV6',
                  'ftransit18-G24AdP6r',
                  'ftransit18-qmLP7A-b',
                  'ftransit18-FFP5b34o',
                  'ftransit18-NwBMLo3Z',
                  'ftransit18-iu1Vj2Oa',
                  'ftransit18-klUp8BS4',
                  'ftransit18-rsXWUN8X',
                  'ftransit18-Y0vPhBVF',
                  'ftransit18-xyp1rU0h',
                  'ftransit18-5SiNC94w',
                  'ftransit18-eXJDDYmE',
                  'tsienna20-YwrRNr9n',
                  'tsienna20-HykkFbXf',
                  'tsienna20-KHB_Cd9k',
                  'tsienna20-TibS83Qr',
                  'tsienna20-cI285Gon',
                  'tsienna20-uIHdpQ9y',
                  'tsienna20-QIMXlb0L',
                  'tsienna20--a2RmRcs',
                  'tsienna20-qA3aAUUq',
                  'tsienna20-1n_z8bYy',
                  'tsienna20-ouPvuX-j',
                  'tsienna20-4ihRwDkS',
                  'tsienna20-65mfPdRD',
                  'tsienna20-Ia0SGJ6z',
                  'tsienna20-TI4TVvT9',
                  'tsienna20-U_FqYq-a',
                ],
                [TIRES_TAB_KEY]: [
                  'fesc20-YPcJPAZE',
                  'fesc20-W6XrryMO',
                  'all-HWSQ9Svy',
                  'all-1pqg1sU3',
                  'all-Pj-K822I',
                  'all-9cw4tn4s',
                ],
                [OTHER_TAB_KEY]: ['all-sLu0CfOt', 'all-98d26745', 'all-z5hfaeP1', 'all-526bc540'],
                [TabKeys.Interior]: [
                  'all-9c2d7b19',
                  'all-qhKA2z',
                  'all-35104fb6',
                  'all-e7663823',
                  'all-ed8a75b6',
                  'all-1c89235c',
                  'all-50eebfb7',
                  'all-T4HrF8KA',
                  'all-jhCxAV6K',
                  'all-ad16621c',
                  'all-rSvk2C',
                  'all-7af3fb1f',
                  'all-0q1tiyhv',
                  'all-5pOlJ8JQ',
                  'all-602375f5',
                  'all-6f747948',
                  'all-2ZcbN2Vv',
                  'all-ce56c439',
                ],
              }}
            />
          </div>
        ) : (
          <div className={styles['tempMessage']}>
            Pass an inspection ID in the URL parameters to be able to edit an inspection.
          </div>
        )}
      </Modal>
      <InspectionListFilters
        title={t(STATUS_TRANSLATION_KEYS[status ?? 'all'])}
        filters={filters}
        filterByDate={filterByDate}
        filterBySearchInput={filterBySearchInput}
        filterByStatuses={filterByStatuses}
        showStatusFilter={status === null}
        isListLoading={itemsLoading.isLoading || totalLoading.isLoading}
        onExportToCsv={exportToCsv}
        exportToCsvLoading={exportToCsvLoading}
      />
      <Table striped highlightOnHover withBorder>
        <thead>
          <tr>
            <th className={styles['actionIconTh']}></th>
            <th>
              <div className={styles['thContent']}>{t('inspectionList.table.vin')}</div>
            </th>
            {showStatusColumn && (
              <th>
                <div className={styles['thContent']}>{t('inspectionList.table.status')}</div>
              </th>
            )}
            <th
              onClick={() => handleSortClick(SortByProperty.LAST_UPDATED)}
              className={clsx({ [styles['clickableTh']]: !paginationDisabled })}
            >
              <div className={styles['thContent']}>
                {t('inspectionList.table.lastUpdated')}
                {filters.sortBy === SortByProperty.LAST_UPDATED && sort}
              </div>
            </th>
          </tr>
        </thead>
        <tbody className={styles['tbody']}>
          {(itemsLoading.isLoading || totalLoading.isLoading) && (
            <tr className={styles['loaderTr']}>
              <td>
                <LoadingOverlay
                  visible={true}
                  overlayBlur={2}
                  color='dark'
                  loader={<Loader color='black' />}
                />
              </td>
            </tr>
          )}
          {(itemsLoading.isLoading || totalLoading.isLoading) && inspections.length === 0 && (
            <tr>
              <td colSpan={maxColSpan}>
                <div className={styles['emptyFiller']}></div>
              </td>
            </tr>
          )}
          {(itemsLoading.error || totalLoading.error) && (
            <tr>
              <td colSpan={maxColSpan} className={clsx(styles['messageTd'], styles['error'])}>
                {t('inspectionList.table.error')}
              </td>
            </tr>
          )}
          {!itemsLoading.isLoading && !totalLoading.error && inspections.length === 0 && (
            <tr>
              <td colSpan={maxColSpan} className={styles['messageTd']}>
                <div>{t('inspectionList.table.empty')}</div>
              </td>
            </tr>
          )}
          {!itemsLoading.isLoading &&
            !totalLoading.error &&
            inspections.map((inspection) => (
              <tr key={inspection.inspectionId}>
                <td className={styles['actionIconTd']}>
                  {isInspectionReviewAvailable(inspection) && (
                    <ActionIcon
                      variant='subtle'
                      color='dark'
                      onClick={() => openInspectionReview(inspection)}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                  )}
                  <ActionIcon
                    variant='subtle'
                    color='dark'
                    onClick={() => openInspectionDetails(inspection)}
                  >
                    <IconArrowsDiagonal size={14} />
                  </ActionIcon>
                </td>
                <td>{inspection.vin ?? <NoData />}</td>
                {showStatusColumn && (
                  <td>
                    {inspection.status ? t(STATUS_TRANSLATION_KEYS[inspection.status]) : <NoData />}
                  </td>
                )}
                <td>{formatLastUpdated(inspection.lastUpdated) ?? <NoData />}</td>
              </tr>
            ))}
        </tbody>
      </Table>
      <div
        className={clsx(styles['paginationContainer'], {
          [styles['loading']]: itemsLoading.isLoading || totalLoading.isLoading,
        })}
      >
        <div>{t('inspectionList.table.rowsPerPage')}</div>
        <Select
          value={pagination?.rowsPerPage.toString() ?? '25'}
          data={['25', '50', '75', '100']}
          variant='unstyled'
          color='dark'
          onChange={selectRowsPerPage}
          disabled={paginationDisabled}
          rightSection={<IconChevronDown size='1rem' />}
          classNames={{
            root: styles['rowsPerPageSelect'],
            rightSection: styles['rowsPerPageSelectRightSection'],
            item: styles['rowsPerPageSelectItem'],
          }}
        />
        <div>
          {pagination?.start ?? 0}-{pagination?.end ?? 0}
        </div>
        <div className={styles['of']}>{t('inspectionList.table.of')}</div>
        <div>{pagination?.totalItems ?? 0}</div>
        <ActionIcon
          variant='transparent'
          color='dark'
          disabled={!pagination || pagination.start < 2 || paginationDisabled}
          onClick={goToPreviousPage}
          className={styles['previousPage']}
        >
          <IconCaretLeftFilled />
        </ActionIcon>
        <ActionIcon
          variant='transparent'
          color='dark'
          disabled={!pagination || pagination.end >= pagination.totalItems || paginationDisabled}
          onClick={goToNextPage}
        >
          <IconCaretRightFilled />
        </ActionIcon>
      </div>
    </div>
  );
}
